/**
 * Single-read fetch wrappers.
 *
 * Why this exists:
 *   Calling `await response.json()` more than once on the same Response object
 *   throws "Failed to execute 'json' on 'Response': body stream already read".
 *   Common triggers: error/success branches both call .json(), or third-party
 *   instrumentation (Sentry, service workers) consumes the body before us.
 *
 * Pattern: read the body as text exactly once, then JSON.parse only if the
 * payload looks like JSON. Empty body (204 No Content, 500 with no payload)
 * is returned as `null`.
 */

export async function readJsonOnce(response) {
  // Body is read here exactly once — this consumes the stream.
  let text = '';
  try {
    text = await response.text();
  } catch {
    return null;
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    // Not JSON — return raw string so callers can surface it as an error.
    return text;
  }
}

/**
 * Convenience wrapper: makes the fetch + reads body once + returns
 * a stable `{ ok, status, data }` shape. Use for any new auth/api call.
 */
export async function safeJsonFetch(url, options = {}) {
  const response = await fetch(url, options);
  const data = await readJsonOnce(response);
  return { ok: response.ok, status: response.status, data };
}
